import React, { useState, useMemo, useEffect } from 'react';
import heart from "./images/heart.png";
import { GameStyles } from "./game-styles.js";
import { useUserAuth } from '../../UserAuthContext.js';
import SignInSignUpModal from '../../Modals/survey/signInSignUpModal.js';
import { DestinationRankings } from "../../Helpers/survey-helpers/suggestion-algorithm/suggestion-data.js";
import DisplayResults from '../../Helpers/game-helpers/DisplayResults.js';
import PostGameData from '../../Helpers/game-helpers/PostGameData.js';
import { imageData } from './imageData_clip.js';
import { destinationData } from './destinationData_clip.js'


const usedPairs = new Set();
const imageBank = Array.from({length: 50}, (_, i) => i);
let lastUsedPairs = [];

const excludedAttributes = new Set([
  'cost_flight', 'cost_hotel', 'cost_food', 'cost_activities', 'cost_week', 'image', 'travel_type'
]);

let userVector = Array(512).fill(0);
let rankings = Array(600).fill("");

//Get 20 pairs without repeating
function generateStartingPairs() {
  //Control amount of image pairs to show
  var rounds = imageBank.length/2;

  var imageSet1 = [];
  var imageSet2 = [];
  const tempImageBank = [...imageBank];

  //Assign random images from bank to set1 and remove from bank as used
  while (imageSet1.length < rounds) {
    var rand = Math.floor(Math.random() * tempImageBank.length);
    imageSet1.push(tempImageBank[rand]);
    tempImageBank.splice(rand, 1);
  }

  //Same for set2
  while (imageSet2.length < rounds) {
    var rand = Math.floor(Math.random() * tempImageBank.length);
    imageSet2.push(tempImageBank[rand]);
    tempImageBank.splice(rand, 1);
  }

  return {imageSet1, imageSet2};
}

//Get 2 unique indeces that have not been paired
function getNextPair() {
  let pair;
  let img1, img2;

  do {
    const index1 = Math.floor(Math.random() * imageBank.length);
    let index2;
    do {
      index2 = Math.floor(Math.random() * imageBank.length);
    } while (index1 === index2);

    img1 = imageBank[index1];
    img2 = imageBank[index2];
    pair = img1 < img2 ? `${img1}-${img2}` : `${img2}-${img1}`;
  } while (
    usedPairs.has(pair) || 
    lastUsedPairs.some(p => p.includes(img1.toString()) || 
    p.includes(img2.toString()))
  );

  usedPairs.add(pair);

  lastUsedPairs.push(pair);
  if (lastUsedPairs.length > 5) {
    lastUsedPairs.shift();
  }

  return pair.split('-').map(Number);
}

function getCosineSimilarity(vecA, vecB) {
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
  }
  
  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

function calculateResults() {
  const rawResults = [];
  let minRaw = Infinity;
  let maxRaw = -Infinity;

  // Loop through every city, find raw similarities, and track min/max bounds
  for (const [cityName, cityData] of Object.entries(destinationData)) {
    const rawSimilarity = getCosineSimilarity(userVector, cityData.vector);
    
    // Update bounds
    if (rawSimilarity < minRaw) minRaw = rawSimilarity;
    if (rawSimilarity > maxRaw) maxRaw = rawSimilarity;

    rawResults.push({
        name: cityName,
        country: cityData.country,
        rawScore: rawSimilarity // Saved as rawScore for the next step
    });
  }

  // Apply min-max scaling to make match percentages scale better
  const targetMax = 98.0;
  const targetMin = 1.0;
  
  const finalResults = rawResults.map(item => {
    // Prevent division by zero
    const range = (maxRaw - minRaw) === 0 ? 1 : (maxRaw - minRaw); 
    
    // Formula
    const scaledScore = ((item.rawScore - minRaw) / range) * (targetMax - targetMin) + targetMin;
    
    return {
        name: item.name,
        country: item.country,
        matchStrength: parseFloat(scaledScore.toFixed(1))
    };
  });
  
  return finalResults.sort((a, b) => b.matchStrength - a.matchStrength);
}


export default function Home() {
  const [currentPair, setCurrentPair] = useState([0,0]);
  const [images, setImages] = useState([imageData[0].image, imageData[0].image]);
  const [imageSets, setImageSets] = useState(generateStartingPairs());
  const [showingUniquePairs, setShowingUniquePairs] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectionCounts, setSelectionCounts] = useState({});
  const [attributeTotals, setAttributeTotals] = useState({});
  
  const [showResults, setShowResults] = useState(false);
  const [resultsPosted, setResultsPosted] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [resultDocId, setResultDocId] = useState(null);

  const { userName, userEmail, user, setHasGameResults } = useUserAuth();
  const [showSignInSignUpModal, setShowSignInSignUpModal] = useState(false);

  useEffect(() => {
    if (!showPopup && !showSignInSignUpModal) {
      handleNextPair();
    }
  }, [showPopup, showSignInSignUpModal]);

  useEffect(() => {
    const [id1, id2] = currentPair;
    setImages([imageData[id1].image, imageData[id2].image]);
  }, [currentPair]);

  const handleNextPair = () => {
    if (showingUniquePairs) {
      if (currentIndex < imageSets.imageSet1.length) {
        const img1 = imageBank[imageSets.imageSet1[currentIndex]];
        const img2 = imageBank[imageSets.imageSet2[currentIndex]];
        const pair = img1 < img2 ? `${img1}-${img2}` : `${img2}-${img1}`;
        usedPairs.add(pair);
        setCurrentPair(pair.split('-').map(Number));
        setCurrentIndex(currentIndex + 1);
      }
      else {
        setShowingUniquePairs(false);
        const nextPair = getNextPair();
        setCurrentPair(nextPair);
      }
    }
    else {
      const nextPair = getNextPair();
      setCurrentPair(nextPair);
    }
  };

  const updateSelectionCounts = (selectedImageId, notSelectedImageId) => {
    setSelectionCounts(prevCounts => {
      const newCounts = { ...prevCounts };
  
      // Log selected image
      if (!newCounts[selectedImageId]) {
        newCounts[selectedImageId] = { selected: 0, notSelected: 0 };
      }
      newCounts[selectedImageId].selected += 1;
  
      // Log not selected image
      if (!newCounts[notSelectedImageId]) {
        newCounts[notSelectedImageId] = { selected: 0, notSelected: 0 };
      }
      newCounts[notSelectedImageId].notSelected += 1;
  
      return newCounts;
    });
  };

  /* New algorithm function */
  const updateAttributeTotals = (selectedImageId, notSelectedImageId) => {
    const selectedImgVector = imageData[selectedImageId].vector;
    const notSelectedImgVector = imageData[notSelectedImageId].vector;
    // Shift user vector closer to selected image's vibe
    userVector = userVector.map((val, i) => val + selectedImgVector[i]);
    // Shift away from not selected image's vibe
    userVector = userVector.map((val, i) => val - notSelectedImgVector[i]);
  }
  
  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  
    const selectedImageId = currentPair[index];
    const notSelectedImageId = currentPair[1 - index];
  
    updateSelectionCounts(selectedImageId, notSelectedImageId);
    updateAttributeTotals(selectedImageId, notSelectedImageId);
  
    setTimeout(() => {
      handleNextPair();
      setSelectedImageIndex(null);
    }, 800);
  };

  useEffect(() => {
    if (showResults) {
      setHasGameResults(true);
    }
  }, [showResults, setHasGameResults]);

  const handleViewResults = async () => {
    if (!showResults) {
      setShowResults(true);
      rankings = calculateResults();
      console.log(rankings);
      var docId = null;
      if (user) {
        docId = await PostGameData(user.uid, attributeTotals, selectionCounts, rankings);
      } else {
        docId = await PostGameData(null, attributeTotals, selectionCounts, rankings);
      }
      setResultDocId(docId);
      setResultsPosted(true);
    } else {
      setShowResults(false);
    }
  }

  const handleAuth = async (userId) => {
    if (showResults) {
      console.log("Posting data");
      const docId = await PostGameData(userId, attributeTotals, selectionCounts, rankings);
      setResultDocId(docId);
      setShowSignInSignUpModal(false);
    } else {
      setShowSignInSignUpModal(false);
    }
  };

  return (
    <div>

      {showResults ? (
         showSignInSignUpModal ? (
          <SignInSignUpModal 
            onClose={() => {
              setShowSignInSignUpModal(false);
            }} 
            handleAuth={handleAuth}
            heading="Sign up to save your results!" 
          />
        ) : (
          <div>
            <DisplayResults
              rankings={rankings}
              DestinationRankings={DestinationRankings}
              handleViewResults={handleViewResults}
              showSignUpModal={setShowSignInSignUpModal}
              user={user}
              docId={resultDocId}
            />
            <GameStyles>
              <div className="button-container">
                <button onClick={() => handleViewResults()} className="continue-button">
                  Continue Selecting
                </button>
              </div>
            </GameStyles>
          </div>
        )
      ) : (
    
        <GameStyles>
        
          {showPopup ? (
            <div className="popup-container">
              <div className="popup-text">
                <h1>Destination Game</h1>
                <p>
                  Choose which image is more appealing to you. It could be based on beauty, 
                  the activities depicted in the picture, or any other reason you prefer it!
                </p>
                <p>
                  Your results will be available after 10 selections, but you can always
                  continue selecting to improve their accuracy. 
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPopup(false);
                }}
                className="popup-button"
              >
                Okay
              </button>
            </div>
          ) : showSignInSignUpModal ? (
            <div>
              <SignInSignUpModal 
                onClose={() => setShowSignInSignUpModal(false)} 
                handleAuth={handleAuth} 
                heading="It's better with an account"
              /> 
            </div>
          ) : (
            <div className="game-wrapper">
              <h1>Destination Game</h1>
              <h3>A "This or That" style game returning destination suggestions based on your preferences</h3>
              <div className="images-wrapper">
                <div onClick={() => handleImageClick(0)} className="image-container" id="left-image">
                  <img 
                    src={images[0]} 
                    alt={`Image ${currentPair[0]}`} 
                    className={`pair-image ${selectedImageIndex === 0 ? "selected" : selectedImageIndex === 1 ? "not-selected" : ""}`}
                    id="left-image"
                  />
                    {selectedImageIndex === 0 && <img src={heart} className="heart animate" alt="heart" />}
                </div>
                {usedPairs.size >= 11 && 
                  (<button
                    onClick={() => handleViewResults()} 
                    className="results-button">View results
                  </button>)
                }
                <div onClick={() => handleImageClick(1)} className="image-container" id="right-image">
                  <img src={images[1]}
                    alt={`Image ${currentPair[1]}`}
                    className={`pair-image ${selectedImageIndex === 1 ? "selected" : selectedImageIndex === 0 ? "not-selected" : ""}`}
                    id="right-image"
                  />
                    {selectedImageIndex === 1 && <img src={heart} className="heart animate" alt="heart" />}
                </div>
              </div>
            </div>
          )}
        </GameStyles>

      )}

    </div>
  );
}
